import { createElement, useState } from 'react';
import cn from 'classnames';
import styles from './styles.scss';
import { useMounted } from 'hooks';

type ButtonSize = 'large' | 'medium' | 'small';
type ButtonAppearance =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'quaternary'
  | 'quinary'
  | 'link'
  | 'whitePillPrimary'
  | 'whitePill'
  | 'whitePillSelected'
  | 'roundedCorner'
  | 'borderless';
type BorderType = 'thick' | 'thin';
type ButtonElement = HTMLButtonElement | HTMLAnchorElement;
type TextTransform = 'none' | 'uppercase' | 'lowercase' | 'capitalize';

export type ButtonProps = React.AnchorHTMLAttributes<ButtonElement> & {
  appearance?: ButtonAppearance;
  size?: ButtonSize;
  invert?: boolean;
  border?: BorderType;
  disabled?: boolean;
  textTransform?: TextTransform;
  element?: 'div';
};

const appearanceStyles = (appearance: ButtonAppearance) => ({
  [styles.primary]: appearance === 'primary',
  [styles.secondary]: appearance === 'secondary',
  [styles.tertiary]: appearance === 'tertiary',
  [styles.quaternary]: appearance === 'quaternary',
  [styles.quinary]: appearance === 'quinary',
  [styles.link]: appearance === 'link',
  [styles.whitePillPrimary]: appearance === 'whitePillPrimary',
  [styles.whitePill]: appearance === 'whitePill',
  [styles.whitePillSelected]: appearance === 'whitePillSelected',
  [styles.roundedCorner]: appearance === 'roundedCorner',
  [styles.borderless]: appearance === 'borderless'
});

const sizeStyles = (size: ButtonSize) => ({
  [styles.large]: size === 'large',
  [styles.medium]: size === 'medium',
  [styles.small]: size === 'small'
});

const borderStyles = (border?: BorderType) => ({
  [styles.thick]: border === 'thick',
  [styles.thin]: border === 'thin',
  [styles.border]: border !== undefined
});

const textTransformStyles = (transform?: TextTransform) => ({
  [styles.upperCase]: transform === 'uppercase',
  [styles.lowerCase]: transform === 'lowercase',
  [styles.capitalize]: transform === 'capitalize'
});

/**
 * This button component is intended to be used within components
 * that belong to RED3 projects
 */
export const Button = ({
  appearance = 'primary',
  children,
  invert = false,
  size = 'large',
  border,
  disabled,
  className,
  textTransform = 'none',
  onClick,
  element,
  type = 'button',
  ...props
}: ButtonProps) => {
  const handleOnClick = (e: React.MouseEvent<ButtonElement>) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    if (onClick) {
      onClick(e);
    }
  };
  const _className = cn(
    styles.base,
    {
      ...appearanceStyles(appearance),
      ...sizeStyles(size),
      ...borderStyles(border),
      ...textTransformStyles(textTransform),
      [styles.disabled]: disabled,
      [styles.invert]: invert
    },
    className
  );
  return createElement(
    element || props.href ? 'a' : 'button',
    { className: _className, onClick: handleOnClick, type, ...props },
    children
  );
};

export interface LoadingButtonProps extends ButtonProps {
  loading?: boolean;
  onClick?: (e: React.MouseEvent<ButtonElement>) => Promise<unknown>;
  hideButtonWhileLoading?: boolean;
}
export const LoaderButton = ({
  loading: _loading,
  className,
  onClick,
  disabled: _disabled,
  hideButtonWhileLoading,
  ...props
}: LoadingButtonProps) => {
  const isMounted = useMounted();

  const [internalLoading, setInternalLoading] = useState(_loading);

  const loading = internalLoading || _loading;
  const disabled = _disabled || internalLoading || loading;

  const setLoading = () => setInternalLoading(true);
  const setDone = () => isMounted() && setInternalLoading(false);

  const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!onClick || disabled) {
      return;
    }

    setLoading();
    const promise = onClick(e);
    if (!(promise instanceof Promise)) {
      return Promise.resolve().then(setDone);
    }
    return promise.then(setDone).catch(setDone);
  };

  const isHidden = hideButtonWhileLoading && loading;

  return (
    <Button
      className={cn(className, { [styles.loading]: loading, [styles.hideButton]: isHidden })}
      {...{ ...props, disabled, onClick: handleOnClick }}
    />
  );
};
